import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

const EDIT_STYLE_ID = 'gsec-letter-edit-style';

function serializeLetterHtml(doc) {
  if (!doc?.documentElement) return '';
  const clone = doc.documentElement.cloneNode(true);
  clone.querySelectorAll('[contenteditable]').forEach((el) => {
    el.removeAttribute('contenteditable');
    el.removeAttribute('spellcheck');
  });
  const editStyle = clone.querySelector(`#${EDIT_STYLE_ID}`);
  if (editStyle) editStyle.remove();
  return `<!DOCTYPE html>\n${clone.outerHTML}`;
}

function enableLetterEditing(doc) {
  if (!doc) return;
  const letter = doc.querySelector('.letter');
  if (letter) {
    letter.setAttribute('contenteditable', 'true');
    letter.setAttribute('spellcheck', 'true');
  }
  if (!doc.getElementById(EDIT_STYLE_ID)) {
    const style = doc.createElement('style');
    style.id = EDIT_STYLE_ID;
    style.textContent = `
      .letter[contenteditable="true"] {
        cursor: text;
        border-radius: 2px;
      }
      .letter[contenteditable="true"]:hover {
        box-shadow: inset 0 0 0 1px rgba(108, 142, 191, 0.45);
      }
      .letter[contenteditable="true"]:focus {
        outline: 1px dashed #6c8ebf;
        outline-offset: 6px;
      }
      @media print {
        .letter[contenteditable] {
          outline: none !important;
          box-shadow: none !important;
        }
      }
    `;
    doc.head.appendChild(style);
  }
}

/** Previews the GSEC settlement letter; body is editable before print/open. */
const GsecLetterModal = ({ transaction, onClose }) => {
  const iframeRef = useRef(null);
  const originalHtmlRef = useRef('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [dirty, setDirty] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  useEffect(() => {
    if (!transaction?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setHtmlContent('');
    setDirty(false);
    originalHtmlRef.current = '';

    api
      .get(`/gsec/${transaction.id}/letter/html`, { responseType: 'text' })
      .then((res) => {
        if (cancelled) return;
        const html = typeof res.data === 'string' ? res.data : '';
        originalHtmlRef.current = html;
        setHtmlContent(html);
        setIframeKey((k) => k + 1);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          'Failed to load letter.';
        setError(msg);
      });

    return () => {
      cancelled = true;
    };
  }, [transaction?.id]);

  const getCurrentHtml = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (doc?.documentElement) return serializeLetterHtml(doc);
    return htmlContent;
  }, [htmlContent]);

  const handleIframeLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    enableLetterEditing(doc);
    const onInput = () => setDirty(true);
    doc.addEventListener('input', onInput);
    // One-shot cleanup when iframe navigates/unloads
    doc.defaultView?.addEventListener(
      'unload',
      () => {
        try {
          doc.removeEventListener('input', onInput);
        } catch (_) {
          /* ignore */
        }
      },
      { once: true }
    );
  }, []);

  if (!transaction) return null;

  /** Open letter HTML in a blank window (no blob: URL in Chrome print headers). */
  const openLetterWindow = (html) => {
    const w = window.open('', '_blank');
    if (!w) return null;
    w.document.open();
    w.document.write(html);
    w.document.close();
    try {
      w.document.title = '\u00A0';
    } catch (_) {
      /* ignore */
    }
    return w;
  };

  const handlePrint = () => {
    const html = getCurrentHtml();
    if (!html) return;
    try {
      const w = openLetterWindow(html);
      if (!w) return;
      setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch (_) {
          /* ignore */
        }
      }, 300);
    } catch (_) {
      try {
        const win = iframeRef.current?.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (__) {
        /* ignore */
      }
    }
  };

  const handleOpenNewTab = () => {
    const html = getCurrentHtml();
    if (!html) return;
    openLetterWindow(html);
  };

  const handleReset = () => {
    if (!originalHtmlRef.current) return;
    setHtmlContent(originalHtmlRef.current);
    setDirty(false);
    setIframeKey((k) => k + 1);
  };

  const dealLabel = transaction.deal_number || transaction.dealNumber || `#${transaction.id}`;
  const txType = String(transaction.transaction_type || transaction.transactionType || '').toUpperCase();
  const fundMovement = String(transaction.fund_movement || transaction.fundMovement || 'no').toLowerCase();
  const scenarioBasis = (() => {
    const isBuy = txType === 'BUY';
    if (isBuy) return fundMovement === 'yes' ? 'RVP (Buy with funds)' : 'RVF (Buy without funds)';
    return fundMovement === 'yes' ? 'DVP (Sell with funds)' : 'DVF (Sell without funds)';
  })();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          width: '100%',
          maxWidth: 880,
          height: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px 18px',
            borderBottom: '1px solid #e2e6ea',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: '#f8f9fa',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 16 }}>
              GSEC Instruction Letter — {dealLabel}
            </div>
            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
              Scenario: <strong>{scenarioBasis}</strong>
              <span style={{ marginLeft: 10, color: '#6c757d' }}>
                · Click the letter text to edit before printing
                {dirty ? <span style={{ color: '#0d6efd', marginLeft: 6 }}>(edited)</span> : null}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleReset}
              disabled={!htmlContent || !dirty}
              title="Restore the original generated letter"
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleOpenNewTab}
              disabled={!htmlContent}
              title="Open in a new tab"
            >
              Open
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handlePrint}
              disabled={!htmlContent}
              title="Print the letter — in the print dialog, uncheck Headers and footers to hide URL and page numbers"
            >
              Print
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => onClose?.()}
            >
              Close
            </button>
          </div>
        </div>

        <div style={{ flex: 1, position: 'relative', background: '#eceff1' }}>
          {loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#555',
              }}
            >
              Loading letter…
            </div>
          )}
          {error && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                left: 12,
                right: 12,
                padding: 12,
                background: '#fdecea',
                color: '#b00020',
                border: '1px solid #f5c2c0',
                borderRadius: 4,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
          {!loading && htmlContent && (
            <iframe
              key={iframeKey}
              ref={iframeRef}
              title="GSEC Letter Preview"
              srcDoc={htmlContent}
              onLoad={handleIframeLoad}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: '#fff',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GsecLetterModal;
